import { useEffect, useState, type FunctionComponent } from 'react';
import { useInView } from 'react-intersection-observer';
import { twJoin } from 'tailwind-merge';

import { useTrackViewedElement } from '@/core/tracking/hooks/useTrackViewedElement';
import { useLazyReducer } from '@/domains/core/applicationState/hooks/useLazyReducer';
import { ListingCampaignCard } from '@/domains/productDiscovery/CommercialAnimation/components/ListingCampaignCards/components/ListingCampaignCard';

const merchandizingCampaignsReducer = () =>
  import(
    '@/domains/productDiscovery/Listings/modules/MerchandizingBanners/MerchandizingBanners.reducer'
  ).then((mod) => mod.merchandizingBannerReducer);

export interface ListingCampaignCardsProps {
  middleDisabled?: boolean;
  middleClassName?: string;
  bottomClassName?: string;
  bottomDisabled?: boolean;
  onViewed?: () => void;
}

export const ListingCampaignCards: FunctionComponent<
  ListingCampaignCardsProps
> = ({
  middleDisabled = false,
  middleClassName,
  bottomClassName,
  bottomDisabled = false,
  onViewed,
}) => {
  // trigger 300px before element in view
  const [middleRef, middleInView] = useInView({ rootMargin: '300px 0px' });
  const [showMiddle, setShowMiddle] = useState(middleInView);

  const [bottomRef, bottomInView] = useInView({ rootMargin: '300px 0px' });
  const [showBottom, setShowBottom] = useState(bottomInView);

  const [middleContainerRef] = useTrackViewedElement(
    () => {
      if (onViewed) {
        onViewed();
      }
    },
    { skip: !onViewed },
  );
  const [bottomContainerRef] = useTrackViewedElement(
    () => {
      if (onViewed) {
        onViewed();
      }
    },
    { skip: !onViewed },
  );

  useEffect(() => {
    if (middleInView && !showMiddle) {
      setShowMiddle(true);
    }
  }, [middleInView, showMiddle, onViewed]);

  useEffect(() => {
    if (bottomInView && !showBottom) {
      setShowBottom(true);
    }
  }, [bottomInView, showBottom, onViewed]);

  useLazyReducer(
    'productDiscovery.merchandizingBanners',
    merchandizingCampaignsReducer,
    {
      condition: middleInView || bottomInView,
    },
  );

  const twCardClasses =
    'relative rounded-card border border-foundation-primary aspect-[328/393] min-[543px]:aspect-[760/822] desktop:aspect-auto';
  const twContainerClasses = 'absolute w-full h-full';

  return (
    <>
      {!middleDisabled && (
        <div
          ref={middleRef}
          className={twJoin(middleClassName, twCardClasses)}
          data-testid="parent-middle-banner"
        >
          <div ref={middleContainerRef} className={twContainerClasses}>
            {showMiddle && <ListingCampaignCard type="middle" />}
          </div>
        </div>
      )}
      {!bottomDisabled && (
        <div ref={bottomRef} className={twJoin(bottomClassName, twCardClasses)}>
          <div ref={bottomContainerRef} className={twContainerClasses}>
            {showBottom && <ListingCampaignCard type="bottom" />}
          </div>
        </div>
      )}
    </>
  );
};

ListingCampaignCards.displayName = 'ListingCampaignCards';
